import { createStyles } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { trackEvent } from '@repo/common/analytics';
import { EntityType } from '@repo/common/enums/entityType';
import { useDebounceFn, useMount } from 'ahooks';
import produce from 'immer';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import type { IComment } from '../../api';
import {
	commentsQueryKeyFactory,
	documentsQueryKeyFactory,
	queryClient,
	useAuthUser,
	useCommentList,
	useCreateComment,
	useDeleteComment,
	useSuspenseDocument,
	useUpdateComment,
	useUpdateDocument,
} from '../../api';
import { CommentSidebar } from '../../components/Comment/CommentSidebar';
import EditorWithComments from '../../components/Comment/EditorWithComments';
import { useCommentStoreContext } from '../../components/Comment/hooks';
import EntityPageActions from '../../components/EntityPageLayout/EntityPageActions';
import EntityPageNavBar from '../../components/EntityPageLayout/EntityPageNavBar';
import EntityPageToggles from '../../components/EntityPageLayout/EntityPageToggles';
import {
	PageLayoutContent,
	PageLayoutContentWrapper,
	PageLayoutOuterWrapper,
	PageLayoutWrapper,
} from '../../components/PageLayout';
import type { WithOnlyIdRequired } from '../../lib/typescript';
import { extractErrorMessage } from '../../utils/errorUtils';
import { useParamsIdSuffixUuid } from '../../utils/hook/utils';
import type { DjangoValueType } from '../TemplatePage/types';
import type { IDocumentPageProps } from './types';

const useStyles = createStyles(() => ({
	content: {
		marginBottom: '30vh',
	},
}));

function DocumentPage({ id: propsId }: IDocumentPageProps) {
	const { classes } = useStyles();

	const paramsId = useParamsIdSuffixUuid();
	const id = propsId || paramsId;

	const { user, workspace } = useAuthUser();

	const { data: entity } = useSuspenseDocument({
		id,
		options: {
			gcTime: 1000,
		},
	});

	useMount(() => {
		trackEvent('document/open', {}, user, workspace);
	});

	const { mutateAsync } = useUpdateDocument({
		options: {
			onSuccess: () => {
				trackEvent(
					'document/update',
					{
						id,
					},
					user,
					workspace
				);
			},
			onError: (error, variables) => {
				const field = Object.keys(variables.data).find((key) => key !== 'id');
				const errorMessage = extractErrorMessage(
					error,
					field,
					'Failed to update document'
				);
				showNotification({
					title: 'Update failed',
					message: errorMessage,
					color: 'red',
				});
				queryClient.invalidateQueries({
					queryKey: documentsQueryKeyFactory.byId(id),
				});
			},
		},
	});

	const { run: updateDocument } = useDebounceFn(
		async (key: string, value: DjangoValueType) => {
			await mutateAsync({ data: { id, [key]: value } });
		},
		{ wait: 350 }
	);

	const handleEntityChange = useCallback(
		(key: string) =>
			async (value: DjangoValueType, saveRemotely = true): Promise<void> => {
				if (saveRemotely) {
					updateDocument(key, value);
					trackEvent(`document/${key}/update`, { id }, user, workspace);
				}
			},
		[id, updateDocument, user, workspace]
	);

	// ==== COMMENTS ====
	const {
		handleProseMirrorRemoveComment,
		setCommentIds,
		reorderComments,
		setPlaceholderVisible,
		setUploadingEditorID,
	} = useCommentStoreContext();

	const [removedCommentID, setRemovedCommentID] = useState<string | null>(null);

	const { data } = useCommentList({
		filters: { entity_id: id },
		options: {
			refetchOnMount: 'always',
		},
	});

	const commentThreads = useMemo(
		() =>
			produce({}, (draftState: { [rootID: string]: IComment[] }) => {
				data?.results.forEach((comment) => {
					const { root } = comment;
					if (!root) {
						return;
					}
					if (!draftState[root]) {
						draftState[root] = [comment];
					} else {
						draftState[root].push(comment);
						draftState[root].sort((a, b) =>
							a.created_at < b.created_at ? -1 : 1
						);
					}
				});
			}) as { [rootID: string]: IComment[] },
		[data]
	);

	useEffect(() => {
		setCommentIds(Object.keys(commentThreads) as string[]);
		setPlaceholderVisible(false);
		setUploadingEditorID('');
		reorderComments();
	}, [
		commentThreads,
		reorderComments,
		setCommentIds,
		setPlaceholderVisible,
		setUploadingEditorID,
	]);

	const invalidateCommentQuery = () => {
		queryClient.invalidateQueries({
			queryKey: commentsQueryKeyFactory.list(1, { entity_id: id }),
		});
	};

	const { mutateAsync: createComment } = useCreateComment({
		options: {
			onSuccess: invalidateCommentQuery,
		},
	});

	const { mutateAsync: updateComment } = useUpdateComment({
		options: {
			onSuccess: invalidateCommentQuery,
		},
	});
	const { mutateAsync: deleteComment } = useDeleteComment({
		options: {
			onSuccess: () => {
				invalidateCommentQuery();
				if (removedCommentID) {
					handleProseMirrorRemoveComment(removedCommentID);
					setRemovedCommentID(null);
				}
			},
		},
	});

	const handleCreateComment = useCallback(
		async (data: Partial<Omit<IComment, 'id'>>): Promise<IComment> =>
			createComment({
				data: {
					entity: id,
					...data,
				},
			}),
		[createComment, id]
	);

	const handleUpdateComment = useCallback(
		async (data: WithOnlyIdRequired<IComment>) => {
			await updateComment({
				data,
			});
		},
		[updateComment]
	);

	const handleDeleteComment = useCallback(
		async (commentID: string) => {
			setRemovedCommentID(commentID);
			await deleteComment({ id: commentID });
		},
		[deleteComment]
	);

	// ==================
	if (!entity) {
		return null;
	}

	const source = entity.native_type === 'page' ? 'Confluence' : 'Secoda';

	return (
		<PageLayoutOuterWrapper key={entity.id}>
			<Helmet>
				<title>{entity?.title ?? 'Document'}</title>
			</Helmet>
			<PageLayoutWrapper name="document">
				<PageLayoutContentWrapper name="document">
					{entity && (
						<EntityPageNavBar
							entity={entity}
							actions={<EntityPageActions entity={entity} />}
							toggles={<EntityPageToggles entity={entity} withComment />}
						/>
					)}
					<PageLayoutContent className={classes.content}>
						<EditorWithComments
							key={entity.updated_at}
							entity={entity}
							commentThreads={commentThreads || {}}
							handleCreateComment={handleCreateComment}
							handleUpdateComment={handleUpdateComment}
							handleDeleteComment={handleDeleteComment}
							handleEntityChange={handleEntityChange}
							entityType={EntityType.document}
						/>
					</PageLayoutContent>
				</PageLayoutContentWrapper>
				<CommentSidebar
					entity={entity}
					commentThreads={commentThreads || {}}
					handleCreateComment={handleCreateComment}
					handleUpdateComment={handleUpdateComment}
					handleDeleteComment={handleDeleteComment}
					updateEntity={(
						key: string,
						value: DjangoValueType,
						saveRemotely = true
					) => handleEntityChange(key)(value, saveRemotely)}
					source={source}
					entityType={EntityType.document}
				/>
			</PageLayoutWrapper>
		</PageLayoutOuterWrapper>
	);
}

// Note: Don't make DocumentPage an observer, it causes a lot of reactions.
export default DocumentPage;
